import { round } from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { useBodyweightGoalForm } from "../../../hooks/goals/useBodyweightGoalForm";
import { usePersonalRecordGoalForm } from "../../../hooks/goals/usePersonalRecordGoalForm";
import {
  goalValidation,
  VALID_GOAL_TYPES,
} from "../../../utils/goals-validations";
import SleepInput from "../../inputs/SleepInput";
import Modal from "../../modal-new";
import OptionToggler from "../../OptionToggler";
import SvgIcon from "../../SvgIcon";
import { BodyweightFields } from "./BodyweightFields";
import { PersonalRecordFields } from "./PersonalRecordFields";

const OZ_PER_L = 33.8140227;

// Exported so Periodization's inline Bodyweight/PR forms (see
// manage-milestone-goals-modal.js) use the same kg/lbs toggle options
// instead of a second copy of this list.
export const getUnitsOptions = (t) => [
  { label: t("coaching-portal:addGoal.kg"), value: "metric" },
  { label: t("coaching-portal:addGoal.lbs"), value: "imperial" },
];

const waterUnitOptions = [
  { label: "Liters (L)", value: "metric" },
  { label: "oz", value: "imperial" },
];

const customGoalsIcons = [
  {
    value: "swimming",
  },
  {
    value: "arrow",
  },
  {
    value: "stretching",
  },
  {
    value: "baseball",
  },
  {
    value: "meditate",
  },
  {
    value: "bicycle",
  },
];

// Exported for the same reason as getUnitsOptions above - Periodization's
// inline "+ Add New" Personal Record form needs the same exercise list.
export const buildMovementOptionsArray = (activeUserToRequest, t) => {
  const {
    squat,
    bench,
    deadlift,
    assistant,
    clean = [],
    jerk = [],
    snatch = [],
    overheadPress = [],
    other = [],
  } = activeUserToRequest?.exerciseLibrary || {};

  const movementOptions = [
    {
      label: t("coaching-portal:addGoal.squat"),
      primary: "squat",
      type: "primary",
    },
    {
      label: t("coaching-portal:addGoal.bench"),
      primary: "bench",
      type: "primary",
    },
    {
      label: t("coaching-portal:addGoal.deadlift"),
      primary: "deadlift",
      type: "primary",
    },
    {
      label: t("coaching-portal:addGoal.clean"),
      primary: "clean",
      type: "primary",
    },
    {
      label: t("coaching-portal:addGoal.jerk"),
      primary: "jerk",
      type: "primary",
    },
    {
      label: t("coaching-portal:addGoal.snatch"),
      primary: "snatch",
      type: "primary",
    },
    {
      label: t("coaching-portal:addGoal.overheadPress"),
      primary: "overheadPress",
      type: "primary",
    },
  ];

  squat.forEach((item) => {
    movementOptions.push({
      label: `${item.movement} ${t("coaching-portal:addGoal.squatParenthesis")}`,
      primary: "squat",
      type: "secondary",
      secondary: item.movement,
    });
  });
  bench.forEach((item) => {
    movementOptions.push({
      label: `${item.movement} ${t("coaching-portal:addGoal.benchParenthesis")}`,
      primary: "bench",
      type: "secondary",
      secondary: item.movement,
    });
  });
  deadlift.forEach((item) => {
    movementOptions.push({
      label: `${item.movement} ${t("coaching-portal:addGoal.deadliftParenthesis")}`,
      primary: "deadlift",
      type: "secondary",
      secondary: item.movement,
    });
  });
  clean.forEach((item) => {
    movementOptions.push({
      label: item.movement,
      primary: "clean",
      type: "secondary",
      secondary: item.movement,
    });
  });
  jerk.forEach((item) => {
    movementOptions.push({
      label: item.movement,
      primary: "jerk",
      type: "secondary",
      secondary: item.movement,
    });
  });
  snatch.forEach((item) => {
    movementOptions.push({
      label: item.movement,
      primary: "snatch",
      type: "secondary",
      secondary: item.movement,
    });
  });
  overheadPress.forEach((item) => {
    movementOptions.push({
      label: `${item.movement} ${t("coaching-portal:addGoal.overheadPressParenthesis")}`,
      primary: "overheadPress",
      type: "secondary",
      secondary: item.movement,
    });
  });
  other.forEach((item) => {
    movementOptions.push({
      label: item.movement,
      primary: "other",
      type: "secondary",
      secondary: item.movement,
    });
  });
  assistant.forEach((item) => {
    movementOptions.push({
      label: item.movement,
      primary: "assistant",
      type: "assistant",
      secondary: item.movement,
    });
  });

  return movementOptions;
};

const AddGoal = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const { units, activeUserToRequest, query, assert, flash, clientGoals } =
    locals;
  const { modalState, goalId } = query;

  const currentGoal = clientGoals.find((goal) => goal._id === goalId);
  // The type selector for this modal lives only in the URL (modalState), not
  // in React state — if it ever goes missing (see client-goals.js's own
  // catch handler, or editing a goal whose own `type` is already corrupt),
  // every `{modalState === "X" && ...}` block below renders nothing, but the
  // submit button used to render regardless — letting a coach fire off a
  // request with no type and no details at all. Gating the button on this
  // makes that submission impossible instead of silently corrupting a goal.
  const isValidType = VALID_GOAL_TYPES.includes(modalState);

  const movementOptions = buildMovementOptionsArray(activeUserToRequest, t);
  const unitsOptions = getUnitsOptions(t);

  // State/validation/submit for these two dated types is shared with
  // Periodization's manage-milestone-goals-modal via these two hooks - see
  // each hook's own comment for why. Both are called unconditionally (Rules
  // of Hooks) even though only one branch ever actually renders/submits,
  // matching how targetSteps/targetSleep/etc. below are already declared
  // unconditionally regardless of modalState. `goal` is null unless this is
  // the modalState this hook actually owns, so the other one's prefill/
  // isUnchanged never see a mismatched goal.
  const bodyweightForm = useBodyweightGoalForm({
    goal: modalState === "bodyWeight" ? currentGoal : null,
    units,
    assert,
    flash,
    buildUrl: (id) =>
      id
        ? `/actions/client-goals/edit-goal/${id}?proxyAthlete=${query.athlete}`
        : `/actions/client-goals/add-new-goal?proxyAthlete=${query.athlete}`,
  });
  const personalRecordForm = usePersonalRecordGoalForm({
    goal: modalState === "personalRecord" ? currentGoal : null,
    units,
    movementOptions,
    assert,
    flash,
    buildUrl: (id) =>
      id
        ? `/actions/client-goals/edit-goal/${id}?proxyAthlete=${query.athlete}`
        : `/actions/client-goals/add-new-goal?proxyAthlete=${query.athlete}`,
  });

  const [targetSteps, setTargetSteps] = useState(
    currentGoal ? currentGoal?.details.targetSteps : ""
  );
  const [targetSleep, setTargetSleep] = useState(
    currentGoal ? currentGoal?.details.targetSleep : ""
  );
  const [targetWaterIntake, setTargetWaterIntake] = useState(() => {
    const val = currentGoal?.details?.targetWaterIntake;
    return val != null ? Number(val) : 2;
  });
  const [targetWaterUnits, setTargetWaterUnits] = useState(units);
  const [customName, setCustomName] = useState(
    currentGoal ? currentGoal?.details.customName : ""
  );
  const [customTarget, setCustomTarget] = useState(
    currentGoal ? currentGoal?.details.customTarget : ""
  );
  const [customIcon, setCustomIcon] = useState(
    currentGoal ? currentGoal?.details.customIcon : ""
  );

  // bodyWeight/personalRecord's own state, validation and submit-payload
  // assembly now live entirely in the two hooks above (shared with
  // Periodization's manage-milestone-goals-modal) - this only still handles
  // the 4 undated types.
  const submitGoal = () => {
    if (modalState === "bodyWeight")
      return bodyweightForm.submitBodyweightGoal();
    if (modalState === "personalRecord")
      return personalRecordForm.submitPersonalRecord();

    const startDate = moment().format("YYYY-MM-DD");
    const utcStartDate = Number(moment().format("YYYYMMDD"));

    const details = {};

    if (modalState === "custom") {
      details.customName = customName;
      details.customTarget = customTarget;
      details.customIcon = customIcon;
    }

    if (modalState === "steps") {
      details.targetSteps = targetSteps;
    }

    if (modalState === "sleep") {
      details.targetSleep = targetSleep;
    }

    if (modalState === "waterIntake") {
      const valueL = Number(targetWaterIntake);
      details.targetWaterIntake = valueL;
    }

    const isValidated = goalValidation(modalState, details, assert, flash);

    if (isValidated) {
      const data = {
        startDate,
        utcStartDate,
        type: modalState,
        details,
      };

      const url = currentGoal
        ? `/actions/client-goals/edit-goal/${currentGoal._id}?proxyAthlete=${query.athlete}`
        : `/actions/client-goals/add-new-goal?proxyAthlete=${query.athlete}`;

      submit(url, {
        method: "POST",
        body: {
          ...data,
        },
      });
    }
  };

  const modalTitles = {
    personalRecord: "Personal record goal",
    steps: "Daily step count goal",
    sleep: "Daily sleep goal",
    waterIntake: "Daily water intake goal",
    bodyWeight: "Body weight goal",
    custom: "Custom goal",
  };
  const modalTitle = modalTitles[modalState] || "Set goal";

  return (
    <Modal
      className={"add-goal-modal"}
      title={t("coaching-portal:addGoal.title")}
      toClear={["modalState", "goalId"]}
    >
      <div className="body">
        {modalState === "personalRecord" && (
          <PersonalRecordFields
            t={t}
            movementOptions={movementOptions}
            unitsOptions={unitsOptions}
            units={units}
            selectedExercise={personalRecordForm.selectedExercise}
            onSelectExercise={personalRecordForm.onSelectExercise}
            targetReps={personalRecordForm.targetReps}
            setTargetReps={personalRecordForm.setTargetReps}
            targetLoad={personalRecordForm.targetLoad}
            setTargetLoad={personalRecordForm.setTargetLoad}
            setTargetUnits={personalRecordForm.setTargetUnits}
            targetDate={personalRecordForm.targetDate}
            setTargetDate={personalRecordForm.setTargetDate}
          />
        )}

        {modalState === "custom" && (
          <>
            <div className="form-col">
              <label htmlFor="customName">
                {t("coaching-portal:addGoal.goalName")}
              </label>

              <input
                type="text"
                name="customName"
                placeholder={t("coaching-portal:addGoal.enterName")}
                className="question-input"
                value={customName}
                onChange={(e) => {
                  const { value } = e.target;

                  setCustomName(String(value));
                }}
              />
            </div>
            <div className="form-col">
              <label htmlFor="customTarget">
                {t("coaching-portal:addGoal.goalTarget")}
              </label>

              <input
                type="text"
                name="customTarget"
                placeholder={t("coaching-portal:addGoal.enterTarget")}
                className="question-input"
                value={customTarget}
                onChange={(e) => {
                  const { value } = e.target;

                  setCustomTarget(String(value));
                }}
              />
            </div>

            <div>
              <label htmlFor="selectIcon">
                {t("coaching-portal:addGoal.selectIcon")}
              </label>

              <div className="goal-icons">
                {customGoalsIcons.map((icon) => (
                  <button
                    key={`${icon.value}-icon`}
                    data-button-type={
                      icon.value === customIcon ? "primary" : "secondary"
                    }
                    data-icon={true}
                    onClick={() => {
                      setCustomIcon(icon.value);
                    }}
                  >
                    <SvgIcon src={`/img/new-icons/${icon.value}.svg`} />
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {modalState === "steps" && (
          <div className="form-col">
            <label htmlFor="targetSteps">
              {t("coaching-portal:addGoal.dailyStepCountGoal")}
            </label>

            <input
              type="number"
              name="targetSteps"
              placeholder={t("coaching-portal:addGoal.enterSteps")}
              className="question-input"
              value={targetSteps}
              onChange={(e) => {
                const { value } = e.target;

                setTargetSteps(String(value));
              }}
            />
          </div>
        )}

        {modalState === "sleep" && (
          <div className="form-col">
            <label htmlFor="targetSleep">
              {t("coaching-portal:addGoal.dailySleepCountGoal")}
            </label>

            <SleepInput
              defaultValue={targetSleep}
              isSleepXD={true}
              name={"sleep-goal"}
              setSleepTime={setTargetSleep}
            />
          </div>
        )}

        {modalState === "waterIntake" && (
          <>
            <div className="form-col">
              <label htmlFor="targetWaterIntake">Daily water intake goal</label>
              <div className="form-row">
                <input
                  type="number"
                  name="targetWaterIntake"
                  placeholder={targetWaterUnits === "metric" ? "L" : "oz"}
                  className="question-input"
                  min={0}
                  step={targetWaterUnits === "metric" ? 0.25 : 4}
                  value={
                    targetWaterUnits === "metric"
                      ? targetWaterIntake
                      : round(targetWaterIntake * OZ_PER_L, 0)
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "") {
                      setTargetWaterIntake(0);
                      return;
                    }
                    const num = Number(v);
                    if (Number.isNaN(num)) return;
                    if (targetWaterUnits === "metric") {
                      setTargetWaterIntake(num);
                    } else {
                      setTargetWaterIntake(round(num / OZ_PER_L, 5));
                    }
                  }}
                />
                <div className="metric-toggler">
                  <OptionToggler
                    name="waterUnits"
                    options={waterUnitOptions}
                    defaultValue={targetWaterUnits}
                    onSelectFn={(value) => setTargetWaterUnits(value)}
                  />
                </div>
              </div>
            </div>
            <div className="form-col">
              <label>Common goals</label>
              <div
                className="form-row"
                style={{ flexWrap: "wrap", gap: "8px" }}
              >
                {(targetWaterUnits === "metric"
                  ? [2, 3, 4, 5]
                  : [2, 3, 4, 5].map((L) => round(L * OZ_PER_L, 0))
                ).map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    data-button-type={
                      (
                        targetWaterUnits === "metric"
                          ? targetWaterIntake === preset
                          : round(targetWaterIntake * OZ_PER_L, 0) === preset
                      )
                        ? "primary"
                        : "secondary"
                    }
                    onClick={() =>
                      setTargetWaterIntake(
                        targetWaterUnits === "metric"
                          ? preset
                          : round(preset / OZ_PER_L, 5)
                      )
                    }
                  >
                    {targetWaterUnits === "metric"
                      ? `${preset}L`
                      : `${preset} oz`}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {modalState === "bodyWeight" && (
          <BodyweightFields
            t={t}
            unitsOptions={unitsOptions}
            units={units}
            targetWeight={bodyweightForm.targetWeight}
            setTargetWeight={bodyweightForm.setTargetWeight}
            setTargetUnits={bodyweightForm.setTargetUnits}
            targetDate={bodyweightForm.targetDate}
            setTargetDate={bodyweightForm.setTargetDate}
          />
        )}

        {isValidType ? (
          <div className="flex justify-end">
            <button
              onClick={(e) => {
                e.preventDefault();
                submitGoal();
              }}
              data-button-type="primary"
            >
              {t("coaching-portal:addGoal.set")}
            </button>
          </div>
        ) : (
          <p className="add-goal-modal__invalid-type">
            {t("coaching-portal:addGoal.invalidTypeMessage")}
          </p>
        )}
      </div>
    </Modal>
  );
};

AddGoal.propTypes = {
  locals: PropTypes.any,
};

export default AddGoal;
